import { useEffect, useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import { AnimationMixer, Box3, Group, Mesh, SRGBColorSpace, Vector3, type AnimationClip, type Material, type MeshStandardMaterial } from "three";
import { SkeletonUtils } from "three-stdlib";
import { createGltfLoader } from "../loaders/loaders";

/**
 * Persona estática de ambiente (funcionario o aspirante adicional):
 * clona el GLB del avatar (SkeletonUtils) y reproduce UNA animación
 * (idle / observe / sit) en bucle. Datos de demostración, sin identidad real.
 */

const AVATAR_URL = "/assets/models/avatar.glb";
const CLIP_URLS = {
  idle: "/assets/models/anim_idle.glb",
  observe: "/assets/models/anim_observe.glb",
  sit: "/assets/models/anim_sit.glb",
} as const;

export type PersonPose = keyof typeof CLIP_URLS;

type Gltf = import("three-stdlib").GLTF;
const shared: { avatar?: Promise<Gltf>; clips: Partial<Record<PersonPose, Promise<Gltf>>> } = { clips: {} };

function loadShared(url: string): Promise<Gltf> {
  const loader = createGltfLoader();
  return new Promise((resolve, reject) => loader.load(url, resolve, undefined, reject));
}

function getAvatar(): Promise<Gltf> {
  shared.avatar ??= loadShared(AVATAR_URL);
  return shared.avatar;
}

function getClip(pose: PersonPose): Promise<Gltf> {
  shared.clips[pose] ??= loadShared(CLIP_URLS[pose]);
  return shared.clips[pose]!;
}

function fixMaterials(root: Group): void {
  root.traverse((obj) => {
    if (!(obj as Mesh).isMesh) return;
    const mesh = obj as Mesh;
    mesh.castShadow = true;
    mesh.frustumCulled = false;
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const mat of materials as Material[]) {
      const std = mat as MeshStandardMaterial;
      if (std.map) std.map.colorSpace = SRGBColorSpace;
      if (typeof std.metalness === "number") std.metalness = Math.min(std.metalness, 0.35);
      if (typeof std.roughness === "number") std.roughness = Math.max(std.roughness, 0.45);
      std.needsUpdate = true;
    }
  });
}

interface PersonProps {
  pose: PersonPose;
  position: [number, number, number];
  /** Orientación (rad, convención atan2(x,z)). */
  yaw?: number;
  name?: string;
}

export function Person({ pose, position, yaw = 0, name }: PersonProps) {
  const [model, setModel] = useState<{ scene: Group; scale: number; clip: AnimationClip } | null>(null);
  const mixerRef = useRef<AnimationMixer | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [avatar, clipGltf] = await Promise.all([getAvatar(), getClip(pose)]);
      if (cancelled) return;
      const clone = SkeletonUtils.clone(avatar.scene) as Group;
      fixMaterials(clone);
      const bbox = new Box3().setFromObject(avatar.scene);
      const height = bbox.getSize(new Vector3()).y;
      const scale = height > 0.01 && (height < 1.2 || height > 2.6) ? 1.72 / height : 1;
      const clip = clipGltf.animations[0].clone();
      clip.tracks = clip.tracks.filter((t) => !t.name.endsWith(".scale"));
      setModel({ scene: clone, scale, clip });
    })().catch(() => setModel(null));
    return () => {
      cancelled = true;
    };
  }, [pose]);

  useEffect(() => {
    if (!model) return;
    const mixer = new AnimationMixer(model.scene);
    mixer.clipAction(model.clip).play();
    mixerRef.current = mixer;
    return () => {
      mixer.stopAllAction();
      mixerRef.current = null;
    };
  }, [model]);

  useFrame((_, delta) => mixerRef.current?.update(delta));

  if (!model) return null;
  return (
    <group position={position} rotation={[0, yaw, 0]} name={name ?? `persona-${pose}`}>
      <group scale={model.scale}>
        <primitive object={model.scene} />
      </group>
    </group>
  );
}
